import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Chat Memory Manager Node Schema
 * 
 * Provides operations to manage chat message history:
 * - Get Messages: Retrieve conversation history
 * - Insert Message: Add new messages to history
 * - Delete Message: Remove specific messages
 * - Clear Session: Delete all messages from a session
 */
const schema: NodePropertySchema = {
  nodeType: 'chat-memory-manager',
  version: '1.0.0',
  
  properties: [
    // ==================================================================
    // OPERATION SELECTION
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      options: [
        { label: 'Get Messages', value: 'getMany' },
        { label: 'Insert Message', value: 'insert' },
        { label: 'Delete Messages', value: 'delete' },
        { label: 'Clear Session', value: 'clear' }
      ],
      default: 'getMany',
      required: true,
      description: 'The operation to perform on chat memory'
    },
    
    // ==================================================================
    // SESSION IDENTIFICATION (All Operations)
    // ==================================================================
    {
      key: 'sessionId',
      label: 'Session ID',
      type: 'string',
      required: true,
      description: 'The chat session identifier',
      placeholder: '{{ $node["NodeName"].string }}'
    },
    
    // ==================================================================
    // GET MESSAGES OPTIONS
    // ==================================================================
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      typeOptions: {
        minValue: 1,
        maxValue: 1000
      },
      displayOptions: {
        show: {
          operation: ['getMany']
        }
      },
      description: 'Maximum number of messages to retrieve'
    },
    
    {
      key: 'includeSystemMessages',
      label: 'Include System Messages',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          operation: ['getMany']
        }
      },
      description: 'Whether to include system messages in the results'
    },
    
    // ==================================================================
    // INSERT MESSAGE OPTIONS
    // ==================================================================
    {
      key: 'role',
      label: 'Role',
      type: 'options',
      options: [
        { label: 'User', value: 'user' },
        { label: 'Assistant', value: 'assistant' },
        { label: 'System', value: 'system' }
      ],
      default: 'user',
      displayOptions: {
        show: {
          operation: ['insert']
        }
      },
      required: true,
      description: 'The role of the message sender'
    },
    
    {
      key: 'content',
      label: 'Content',
      type: 'text',
      typeOptions: {
        rows: 4
      },
      displayOptions: {
        show: {
          operation: ['insert']
        }
      },
      required: true,
      description: 'The message content',
      placeholder: '{{ $node["NodeName"].string }}'
    },
    
    {
      key: 'metadata',
      label: 'Metadata (JSON)',
      type: 'json',
      default: '{}',
      displayOptions: {
        show: {
          operation: ['insert']
        }
      },
      description: 'Optional metadata to attach to the message',
      placeholder: '{ "source": "api", "userId": "123" }'
    },
    
    // ==================================================================
    // DELETE MESSAGES OPTIONS
    // ==================================================================
    {
      key: 'deleteCount',
      label: 'Number of Messages',
      type: 'number',
      default: 1,
      displayOptions: {
        show: {
          operation: ['delete']
        }
      },
      description: 'Number of recent messages to delete (leave empty to delete all)',
      placeholder: '1'
    }
  ],
  
  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://docs.evoe2e.com/nodes/chat-memory-manager'
  }
};

export default schema;
